/**
 * Turn whatever we have into one of the four cabin codes search understands.
 *
 *   economy · premium_economy · business · first
 *
 * WHY THIS EXISTS. `bookings.cabin_class` does not hold a cabin code. It holds
 * the FARE BRAND the customer picked — "Basic Economy", "Delta Main Classic",
 * "Choice Preferred", "First". Across the live book on 2026-09-21 there were 34
 * distinct values and only one ("business") was a real code. Meanwhile
 * `flight_data.cabin_class` holds the cabin the SEARCH ran in, which is
 * "economy" for every one of them: premium fares arrive as booking-option
 * upsells on an economy search.
 *
 * Every search path maps a cabin with a lowercase lookup —
 * `{ economy:'1', premium_economy:'2', business:'3', first:'4' }[cabin] || '1'`
 * — so any brand label missed the table and silently searched ECONOMY. For an
 * economy fare that was right by accident. For a premium one it hid the
 * customer's own cabin: trip 250ABB bought United "First", pressed Change
 * Flight, and was offered only Basic and Main. Six live trips were exposed —
 * three First, two Premium Economy, one Delta Premium Select.
 *
 * Economy brands must STAY economy. "Delta Comfort", "Main Plus", "Choice
 * Extra" and friends are premium-sounding economy fares, and Google files them
 * as Economy; moving them up a cabin would show the customer fares they never
 * bought, at prices they never agreed to. So the rule is conservative: only an
 * explicit cabin word, or one of a few named premium products that do not say
 * the word, moves a fare out of economy.
 */

export const CABIN_CODES = Object.freeze(['economy', 'premium_economy', 'business', 'first']);

/**
 * Premium products whose names never say "business" or "first". Kept short and
 * explicit rather than clever — a wrong entry here moves a customer into a
 * cabin they did not buy.
 */
const NAMED_PREMIUM = [
  [/\bdelta one\b/i, 'business'],
  [/\bpolaris\b/i, 'business'],
  [/\bmint\b/i, 'business'],
  [/\bupper class\b/i, 'business'],
  [/\bclub world\b/i, 'business'],
  [/\bpremium select\b/i, 'premium_economy'],
];

/**
 * @param {unknown} value a cabin code, a fare-brand label, or nothing
 * @param {string} [fallback='economy'] returned when nothing matches
 * @returns {'economy'|'premium_economy'|'business'|'first'}
 */
export function normalizeCabinClass(value, fallback = 'economy') {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  const lower = raw.toLowerCase().replace(/[\s-]+/g, '_');
  // Already a code, in any case ("First", "FIRST", "premium economy").
  if (CABIN_CODES.includes(lower)) return lower;

  for (const [re, code] of NAMED_PREMIUM) if (re.test(raw)) return code;

  // Explicit cabin words. First beats business beats premium, so a label like
  // "First / Business" resolves to the higher cabin the customer paid for.
  if (/\bfirst\b/i.test(raw)) return 'first';
  if (/\bbusiness\b/i.test(raw)) return 'business';
  if (/\bpremium\b/i.test(raw)) return 'premium_economy';

  return fallback;
}

/**
 * The fare brand the customer bought, exactly as stored ("First", "Delta Main
 * Classic"), or null when the booking names none.
 *
 * Reads BOTH spellings, camelCase first. The client almost never holds a raw
 * DB row. It holds a FlightBooking entity (src/api/entities.js), which stores
 * the column as `cabinClass` and has NO `cabin_class` mirror, and anything
 * passed through router state is a structured clone of that entity, so it has
 * the same shape. Code that read only `cabin_class` got undefined on every
 * real trip. That is how #64 shipped a Change Flight fix that never reached
 * the button: 250ABB and TVWQEG still searched economy, and the change review
 * page still labelled their First flights "economy".
 */
export function purchasedFareBrand(booking) {
  const v = booking?.cabinClass || booking?.cabin_class;
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/**
 * The cabin a trip is actually in, for a change search.
 *
 * The purchased fare brand on the booking row is the evidence; the search
 * cabin in flight_data is only where the ORIGINAL search happened to run, and
 * is "economy" even for a First fare. So the row wins whenever it names a
 * premium cabin, and flight_data is consulted only when the row says nothing.
 */
export function cabinForChangeSearch(booking) {
  const fromRow = normalizeCabinClass(purchasedFareBrand(booking), null);
  if (fromRow) return fromRow;
  const fd = (booking?.flightData || booking?.flight_data || {}).cabin_class;
  return normalizeCabinClass(fd, 'economy');
}
